import {useCallback, useEffect, useRef, useState} from "react";
import axios from "axios";
import {createIdempotencyKey} from "./rsoSalesForecastHelpers";
import {windowCodeOf} from "./rsoForecastWindowHelpers";
import {isSignInExpired} from "./RsoSignInExpired";

const materialsOf = (draft) => Object.values(draft?.materials || {}).sort((a,b) => String(a.productId).localeCompare(String(b.productId)));
const rawRow = (row) => ({productId:row.productId,name:row.name || row.productName || "",
  unitsTurning:row.unitsTurning == null ? "" : String(row.unitsTurning),
  quantityOverride:row.quantityOverride == null ? "" : String(row.quantityOverride),notes:row.notes || ""});
// Names are catalog display data, not editable draft inputs. The server may return
// today's private name even when an older saved draft submitted a different label.
const signature = (draft) => JSON.stringify(materialsOf(draft).map((row) => {
  const input = rawRow(row);
  return {productId:input.productId,unitsTurning:input.unitsTurning,
    quantityOverride:input.quantityOverride,notes:input.notes};
}));
const emptySignature = "[]";
const empty = () => ({materials:{},review:null,requestId:null});
const paramsFor = (key) => {const [month,id] = key.split(":");return {contactInfoId:Number(id),forecastMonth:`${month}-01`};};
// A save that only removes materials from the saved draft (cleanup after a send) is always accepted.
const onlyRemovesMaterials = (next,saved) => {
  try {
    const nextRows=JSON.parse(next);const savedRows=JSON.parse(saved).map((row)=>JSON.stringify(row));
    return nextRows.length<savedRows.length && nextRows.every((row)=>savedRows.includes(JSON.stringify(row)));
  } catch(_) {return false;}
};
// A key whose month can't be written now keeps its values on screen and is not retried.
const blocked = (meta) => !!meta?.windowBlocked;
const unavailableMessage = "We couldn't confirm the RSO input week. Try again in a minute.";
// Nothing more to try in this flush: refused by the week, or this exact entry just failed as unconfirmed.
const settled = (meta,current) => blocked(meta) || (!!meta?.unavailableSignature && meta.unavailableSignature===signature(current));
// NANCY refused the draft because the sign-in expired. The draft stays unsaved and blocks leaving, so nothing is
// dropped silently; the page shows its sign-in message (with the way to sign in again) instead of Retry.
const signInExpiredState = {saving:false,pending:true,conflict:false,signInExpired:true,error:"Your sign-in has expired. Sign in again."};

// Durable drafts contain raw inputs only. Reviews are always obtained again from the forecast API.
export default function useRsoAccountDrafts({endpoint,config,sameEntry,onPersisted,onWindowRefusal,initialDrafts={}}) {
  const [drafts,renderDrafts] = useState(initialDrafts);
  const [states,setStates] = useState({});
  const values = useRef(initialDrafts);
  const metadata = useRef({});
  const timers = useRef({});
  const alive = useRef(true);
  const persistedCallback = useRef(onPersisted);
  persistedCallback.current = onPersisted;
  const windowRefusalCallback = useRef(onWindowRefusal);
  windowRefusalCallback.current = onWindowRefusal;
  const setState = useCallback((key,change) => {if(alive.current)setStates((previous)=>({...previous,[key]:{...previous[key],...change}}));},[]);
  const setDrafts = useCallback((update) => {
    values.current = typeof update === "function" ? update(values.current) : update;
    if(alive.current)renderDrafts(values.current);
  },[]);
  const persist = useCallback(async (key) => {
    let meta=metadata.current[key];
    if(!meta) {
      if(!Object.keys(values.current[key]?.materials || {}).length)return true;
      try {
        const response=await axios.get(`${endpoint}/draft`,{...config,params:paramsFor(key)});
        const result=response.data;
        if(!Array.isArray(result?.materials) || !Number.isInteger(result.draftRevision))throw new Error("Incomplete draft response");
        const remote={materials:Object.fromEntries(result.materials.map((row)=>[row.productId,rawRow(row)]))};
        const detail=await axios.get(endpoint,{...config,params:paramsFor(key)});
        if(!Number.isInteger(detail.data?.revision))throw new Error("Incomplete forecast response");
        meta={revision:result.draftRevision,baseRevision:result.materials.length ? result.baseForecastRevision : detail.data.revision,
          signature:signature(remote),updatedAt:result.updatedAt,pending:null,conflict:result.materials.length>0 && signature(remote)!==signature(values.current[key])};
        metadata.current[key]=meta;
        if(meta.conflict){setState(key,{pending:true,conflict:true,error:"This property has another saved draft. Your entries are kept. Open the property and compare the saved draft before leaving."});return false;}
      } catch(failure){setState(key,isSignInExpired(failure) ? signInExpiredState
        : {pending:true,error:"Could not load saved progress. Your entries are kept. Retry before leaving."});return false;}
    }
    if(meta.promise) {if(!await meta.promise)return false;return persist(key);}
    clearTimeout(timers.current[key]);
    const nextSignature=signature(values.current[key]);
    if(nextSignature === meta.signature) return true;
    // A revision conflict needs an explicit choice; retrying must never overwrite the other device.
    if(meta.conflict) return false;
    // The input week refused this draft: never block navigation or a send, and don't retry it.
    if(blocked(meta) && !onlyRemovesMaterials(nextSignature,meta.signature)) return true;
    const rows=materialsOf(values.current[key]).map(rawRow);
    if(!meta.pending || meta.pending.signature !== nextSignature) meta.pending={signature:nextSignature,
      body:{...paramsFor(key),expectedDraftRevision:meta.revision,baseForecastRevision:meta.baseRevision,
        materials:rows,requestId:createIdempotencyKey()}};
    const pending=meta.pending;
    setState(key,{saving:true,error:"",pending:true});
    meta.promise=(async()=>{
      try {
        const response=await axios.put(`${endpoint}/draft`,pending.body,config);
        const result=response.data;
        if(!result || !Number.isInteger(result.draftRevision) || !Array.isArray(result.materials)
          || Number(result.contactInfoId)!==pending.body.contactInfoId || result.draftRevision!==pending.body.expectedDraftRevision+1
          || result.forecastMonth.slice(0,7)!==pending.body.forecastMonth.slice(0,7)
          || signature({materials:Object.fromEntries(result.materials.map((row)=>[row.productId,rawRow(row)]))})!==signature({materials:Object.fromEntries(rows.map((row)=>[row.productId,row]))})) throw new Error("Draft save was not confirmed");
        meta.revision=result.draftRevision;meta.signature=pending.signature;meta.pending=null;meta.updatedAt=result.updatedAt;meta.unavailableSignature=null;
        setState(key,{saving:false,error:"",conflict:false,signInExpired:false,updatedAt:result.updatedAt,pending:signature(values.current[key])!==meta.signature});
        persistedCallback.current?.();
        return true;
      } catch(failure) {
        const windowCode=windowCodeOf(failure);
        if(windowCode==="window_unavailable") {
          // NANCY couldn't confirm the week: the draft stays editable and unsaved, and is tried again on the
          // next change or flush. Like every week refusal it never blocks navigation or a send.
          meta.unavailableSignature=pending.signature;
          setState(key,{saving:false,pending:true,conflict:false,readOnly:false,
            error:typeof failure?.response?.data?.message === "string" && failure.response.data.message ? failure.response.data.message : unavailableMessage});
          windowRefusalCallback.current?.(windowCode);
          return true;
        }
        if(windowCode) {
          // A refusal is final and saved nothing, so a later save (once the month can be written) builds a new body.
          meta.windowBlocked=windowCode;meta.pending=null;
          setState(key,{saving:false,pending:false,conflict:false,error:"",readOnly:true,windowCode,
            windowMessage:typeof failure?.response?.data?.message === "string" ? failure.response.data.message : ""});
          windowRefusalCallback.current?.(windowCode);
          return true;
        }
        meta.conflict=failure?.response?.status===409;
        setState(key,isSignInExpired(failure) ? signInExpiredState : {saving:false,pending:true,conflict:meta.conflict,signInExpired:false,error:meta.conflict
          ? "This draft was changed in another tab or device. Your entries are kept here. Compare the saved draft before replacing either version."
          : failure?.response?.data?.message || "Could not save your draft. Your entries are kept here. Retry before leaving."});
        return false;
      } finally {meta.promise=null;}
    })();
    if(!await meta.promise)return false;
    return signature(values.current[key])!==meta.signature && !settled(meta,values.current[key]) ? persist(key) : true;
  },[config,endpoint,setState]);
  const flushDrafts=useCallback(async()=>{
    // Edits can arrive while another property's save is awaiting its response.
    // Recheck the whole workspace before allowing the caller to close it.
    do {
      for(const key of Object.keys(values.current)) if(!await persist(key))return false;
    } while(Object.keys(values.current).some((key)=>!settled(metadata.current[key],values.current[key])
      && signature(values.current[key])!==(metadata.current[key]?.signature || emptySignature)));
    return true;
  },[persist]);
  const loadDraft=useCallback(async(key,detail)=>{
    const previous=metadata.current[key];
    if(previous?.promise && !await previous.promise)throw new Error("Your draft could not be saved. Retry before loading another property.");
    const response=await axios.get(`${endpoint}/draft`,{...config,params:paramsFor(key)});
    const result=response.data;
    if(!result || !Array.isArray(result.materials) || !Number.isInteger(result.draftRevision) || Number(result.contactInfoId)!==Number(detail.contactInfoId) || result.forecastMonth.slice(0,7)!==paramsFor(key).forecastMonth.slice(0,7))throw new Error("Could not load the saved draft. Try again.");
    const remote={...empty(),materials:Object.fromEntries(result.materials.map((row)=>[row.productId,rawRow(row)]))};
    const local=values.current[key];
    const keepLocal=local && (!previous || signature(local)!==previous.signature);
    const selected=keepLocal ? {...remote,...local,materials:{...local.materials},review:null,requestId:null} : remote;
    // A send may have completed even if its response was lost. Saved values are authoritative.
    const materials={...selected.materials};
    for(const row of Object.values(materials)) {
      const saved=detail.entries.find((entry)=>Number(entry.productId)===Number(row.productId));
      if(saved && sameEntry(row,rawRow(saved)))delete materials[row.productId];
    }
    const carriedConflict=keepLocal && (previous?.conflict || (previous ? signature(remote)!==previous.signature : result.materials.length>0 && signature(local)!==signature(remote)));
    metadata.current[key]={revision:result.draftRevision,baseRevision:result.baseForecastRevision ?? detail.revision,
      signature:signature(remote),updatedAt:result.updatedAt,pending:null,conflict:!!carriedConflict,
      ...(previous?.windowBlocked ? {windowBlocked:previous.windowBlocked} : {})};
    if(!Object.keys(remote.materials).length)metadata.current[key].baseRevision=detail.revision;
    setDrafts((current)=>({...current,[key]:{...selected,materials,review:null,requestId:null}}));
    setState(key,{saving:false,error:carriedConflict ? "A saved draft differs from your open entries. Compare both before replacing either version." : "",
      conflict:!!carriedConflict,signInExpired:false,updatedAt:result.updatedAt,pending:signature({materials})!==signature(remote),
      forecastChanged:result.materials.length>0 && result.baseForecastRevision!==detail.revision});
  },[config,endpoint,sameEntry,setDrafts,setState]);
  const advanceRevision=useCallback((key,revision)=>{if(metadata.current[key])metadata.current[key].baseRevision=revision;},[]);
  const discardDraft=useCallback(async(key)=>{
    const meta=metadata.current[key];if(!meta)return false;
    if(meta.promise && !await meta.promise)return false;
    clearTimeout(timers.current[key]);
    const requestId=meta.discardRequestId || createIdempotencyKey();meta.discardRequestId=requestId;
    const discarded=values.current[key];
    setState(key,{saving:true,error:""});
    meta.promise=(async()=>{
      try {
        const response=await axios.delete(`${endpoint}/draft`,{...config,params:{...paramsFor(key),expectedDraftRevision:meta.revision,requestId}});
        const result=response.data;
        if(!Number.isInteger(result?.draftRevision) || result.draftRevision!==meta.revision+1
          || Number(result.contactInfoId)!==paramsFor(key).contactInfoId || result.forecastMonth.slice(0,7)!==paramsFor(key).forecastMonth.slice(0,7)
          || !Array.isArray(result.materials) || result.materials.length)throw new Error("Discard was not confirmed");
        meta.revision=result.draftRevision;meta.signature=emptySignature;meta.pending=null;meta.discardRequestId=null;meta.conflict=false;meta.windowBlocked=null;
        setDrafts((current)=>{
          const materials={...current[key]?.materials};
          for(const [id,row] of Object.entries(discarded?.materials || {}))if(JSON.stringify(materials[id])===JSON.stringify(row))delete materials[id];
          return {...current,[key]:{...empty(),materials}};
        });
        setState(key,{saving:false,pending:signature(values.current[key])!==emptySignature,error:"",conflict:false,signInExpired:false,updatedAt:null,
          readOnly:false,windowCode:null,windowMessage:""});
        persistedCallback.current?.();return true;
      } catch(failure) {
        meta.conflict=failure?.response?.status===409;
        setState(key,isSignInExpired(failure) ? signInExpiredState : {saving:false,pending:true,conflict:meta.conflict,signInExpired:false,error:meta.conflict
          ? "This draft was changed in another tab or device. Your entries are kept. Compare the saved draft before discarding it."
          : "Could not discard the saved draft. Your entries are kept. Retry."});return false;
      } finally {meta.promise=null;}
    })();
    return meta.promise;
  },[config,endpoint,setDrafts,setState]);
  const compareDraft=useCallback(async(key)=>{
    try {const response=await axios.get(`${endpoint}/draft`,{...config,params:paramsFor(key)});
      if(!Array.isArray(response.data?.materials))throw new Error("Invalid draft");
      setState(key,{comparison:response.data});return response.data;
    } catch(failure){setState(key,{error:"Could not retrieve the other saved draft. Your entries are kept. Retry."});return null;}
  },[config,endpoint,setState]);
  const resolveConflict=useCallback((key,useMine)=>{
    const compared=states[key]?.comparison;if(!compared)return;
    const remote={...empty(),materials:Object.fromEntries(compared.materials.map((row)=>[row.productId,rawRow(row)]))};
    metadata.current[key]={revision:compared.draftRevision,baseRevision:compared.baseForecastRevision,
      signature:signature(remote),updatedAt:compared.updatedAt,pending:null,conflict:false};
    setDrafts((current)=>({...current,[key]:useMine ? {...current[key],review:null,requestId:null} : remote}));
    setState(key,{comparison:null,conflict:false,error:"",pending:useMine,updatedAt:compared.updatedAt});
    if(useMine)return persist(key);
    return true;
  },[persist,setDrafts,setState,states]);
  useEffect(()=>{
    for(const key of Object.keys(drafts)) {
      clearTimeout(timers.current[key]);
      const meta=metadata.current[key];
      if(meta && signature(drafts[key])!==meta.signature && !meta.conflict && !blocked(meta)) {
        setState(key,{pending:true});timers.current[key]=setTimeout(()=>persist(key),1000);
      }
    }
    return ()=>Object.values(timers.current).forEach(clearTimeout);
  },[drafts,persist,setState]);
  useEffect(()=>{alive.current=true;return()=>{alive.current=false;Object.values(timers.current).forEach(clearTimeout);};},[]);
  // The input week reopened (or NANCY could confirm it again): let this draft save normally.
  const unblockDraft=useCallback((key)=>{
    const meta=metadata.current[key];if(!blocked(meta))return;
    meta.windowBlocked=null;
    setState(key,{readOnly:false,windowCode:null,windowMessage:"",pending:signature(values.current[key])!==meta.signature});
    if(signature(values.current[key])!==meta.signature && !meta.conflict){clearTimeout(timers.current[key]);timers.current[key]=setTimeout(()=>persist(key),1000);}
  },[persist,setState]);
  const hasPendingDrafts=Object.keys(drafts).some((key)=>!blocked(metadata.current[key])
    && signature(drafts[key])!==(metadata.current[key]?.signature || emptySignature));
  return {drafts,setDrafts,states,hasPendingDrafts,flushDrafts,loadDraft,advanceRevision,discardDraft,compareDraft,resolveConflict,unblockDraft};
}
